import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_ACTIVE_TIME_ENTRY, GET_TOTAL_TIME_WORKED, START_TIME_ENTRY, STOP_TIME_ENTRY } from '../../../graphql/work-order-time';
import { useWorkOrderGPS } from './useWorkOrderGPS';
import LocationWarning from './LocationWarning';
import LocationPermissionHelp from './LocationPermissionHelp';
import { requestLocationPermission } from '../../../services/location.service';
import { LOCATION_PROMPT_SESSION_KEY } from '../../../constants/location-tracking-prompt';

/**
 * WorkOrderGPSTracker - Live GPS-enabled start/stop time tracker
 * Supports start/pause/resume without auto-completing the work order.
 * 
 * @param {Object} props
 * @param {string} props.workOrderId - Work order ID
 * @param {string} props.workOrderStatus - Current work order status
 * @param {Object} props.currentUser - Current user object
 * @param {Function} props.onStatusChange - Callback when work order status changes
 * @param {Object} props.property - Property object with latitude/longitude
 */
function WorkOrderGPSTracker({ workOrderId, workOrderStatus, currentUser, onStatusChange, property }) {
  const [showWarning, setShowWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);
  const [elapsedDisplay, setElapsedDisplay] = useState('0m');
  const timerRef = useRef(null);

  const propertyLocation = property?.latitude && property?.longitude
    ? { latitude: property.latitude, longitude: property.longitude }
    : null;

  const { data: activeTimeData, refetch: refetchActive } = useQuery(GET_ACTIVE_TIME_ENTRY, {
    variables: { workOrderId },
    skip: !workOrderId || !currentUser?.id,
    pollInterval: 30000,
  });

  const { data: totalTimeData, refetch: refetchTotalTime } = useQuery(GET_TOTAL_TIME_WORKED, {
    variables: { workOrderId },
    skip: !workOrderId,
    fetchPolicy: 'network-only',
  });

  const activeTimeEntry = activeTimeData?.activeTimeEntry;
  const totalTimeWorkedMinutes = totalTimeData?.totalTimeWorked || 0;

  const {
    currentLocation,
    isCapturing,
    captureError,
    showDistanceWarning,
    gpsAvailable,
    distanceFromProperty,
    isAtProperty,
    captureLocation,
    clearLocation,
  } = useWorkOrderGPS(propertyLocation);

  useEffect(() => {
    if (!gpsAvailable) return;

    const permissionRequested = sessionStorage.getItem(LOCATION_PROMPT_SESSION_KEY);
    
    if (!permissionRequested && navigator.geolocation) {
      const timer = setTimeout(async () => {
        try {
          const granted = await requestLocationPermission();
          sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
          if (granted) {
            captureLocation(false).catch(() => {});
          }
        } catch (error) {
          // Silently fail
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [gpsAvailable, captureLocation]);

  // Live ticking timer — accumulates time from all previous entries
  useEffect(() => {
    const formatElapsed = (totalSeconds) => {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${String(seconds).padStart(2, '0')}s`;
      }
      if (minutes > 0) {
        return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
      }
      return `${seconds}s`;
    };

    if (activeTimeEntry?.startedAt) {
      const updateElapsed = () => {
        const start = new Date(activeTimeEntry.startedAt);
        const currentSegmentSec = Math.floor((Date.now() - start.getTime()) / 1000);
        const previousSec = totalTimeWorkedMinutes * 60;
        // totalTimeWorked from backend includes the active entry's running time,
        // but it's only updated on fetch — so use max to avoid double-counting
        const totalSec = Math.max(previousSec, currentSegmentSec);
        setElapsedDisplay(formatElapsed(totalSec));
      };

      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else if (totalTimeWorkedMinutes > 0) {
      // Paused — show accumulated total
      setElapsedDisplay(formatElapsed(totalTimeWorkedMinutes * 60));
      if (timerRef.current) clearInterval(timerRef.current);
    } else {
      setElapsedDisplay('0m');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [activeTimeEntry?.startedAt, totalTimeWorkedMinutes]);

  const [startTimeEntry, { loading: starting }] = useMutation(START_TIME_ENTRY, {
    onCompleted: () => {
      toast.success('Work started - timer running');
      clearLocation();
      setShowWarning(false);
      setPendingAction(null);
      refetchActive();
      refetchTotalTime();
      if (onStatusChange && workOrderStatus === 'OPEN') {
        onStatusChange('IN_PROGRESS');
      }
    },
    onError: (error) => {
      toast.error(`Failed to start work: ${error.message}`);
      setPendingAction(null);
    },
  });

  const [stopTimeEntry, { loading: stopping }] = useMutation(STOP_TIME_ENTRY, {
    onCompleted: () => {
      toast.success('Timer paused - time entry saved');
      clearLocation();
      setShowWarning(false);
      setPendingAction(null);
      refetchActive();
      refetchTotalTime();
    },
    onError: (error) => {
      toast.error(`Failed to stop work: ${error.message}`);
      setPendingAction(null);
    },
  });

  const handleStartWork = async () => {
    setPendingAction('start');

    const location = await captureLocation(true);

    if (location && showDistanceWarning && !showWarning) {
      setShowWarning(true);
      return;
    }

    proceedWithStart(location);
  };

  const proceedWithStart = (location) => {
    startTimeEntry({
      variables: {
        input: {
          workOrderId,
          notes: location
            ? `Started at location: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)} (accuracy: ${location.accuracy}m)`
            : 'Started (GPS unavailable)',
          arrivedAtLatitude: location?.latitude,
          arrivedAtLongitude: location?.longitude,
          arrivedAtAccuracy: location?.accuracy,
        },
      },
    });
  };

  const handleStopWork = async () => {
    if (!activeTimeEntry) {
      toast.error('No active time entry found');
      return;
    }

    setPendingAction('stop');

    const location = await captureLocation(false);

    proceedWithStop(location);
  };

  const handleContinueAnyway = () => {
    setShowWarning(false);
    
    if (pendingAction === 'start') {
      proceedWithStart(currentLocation);
    } else if (pendingAction === 'stop') {
      proceedWithStop(currentLocation);
    }
  };

  const proceedWithStop = (location) => {
    if (!activeTimeEntry) {
      toast.error('No active time entry found');
      return;
    }

    stopTimeEntry({
      variables: {
        input: {
          timeEntryId: activeTimeEntry.id,
          notes: location
            ? `Stopped at location: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)} (accuracy: ${location.accuracy}m)`
            : 'Stopped (GPS unavailable)',
          departedAtLatitude: location?.latitude,
          departedAtLongitude: location?.longitude,
          departedAtAccuracy: location?.accuracy,
        },
      },
    });
  };

  const handleCancel = () => {
    setShowWarning(false);
    setPendingAction(null);
    clearLocation();
  };

  const isLoading = starting || stopping || isCapturing;
  const isWorkable = workOrderStatus === 'OPEN' || workOrderStatus === 'IN_PROGRESS';
  const canStart = isWorkable && !activeTimeEntry && !isLoading;
  const canStop = isWorkable && activeTimeEntry && !isLoading;

  if (!isWorkable) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Active Timer Banner */}
      {activeTimeEntry && (
        <div className="bg-green-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">Work in progress</p>
                <p className="text-xs text-green-100">
                  Started: {new Date(activeTimeEntry.startedAt).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white tabular-nums">{elapsedDisplay}</p>
            </div>
          </div>
        </div>
      )}

      {/* Paused Banner — no active entry but has accumulated time */}
      {!activeTimeEntry && workOrderStatus === 'IN_PROGRESS' && totalTimeWorkedMinutes > 0 && (
        <div className="bg-gray-500 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-white">Timer paused</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white tabular-nums">{elapsedDisplay}</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Work Time Tracker</h3>
            {!activeTimeEntry && workOrderStatus === 'IN_PROGRESS' && (
              <p className="text-xs text-gray-500 mt-0.5">Timer paused - tap Start Work to resume</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
          </div>
        </div>

        {/* Warning for distance */}
        {showWarning && currentLocation && (
          <LocationWarning
            currentLocation={currentLocation}
            propertyLocation={propertyLocation}
            onContinue={handleContinueAnyway}
            onCancel={handleCancel}
          />
        )}

        {/* GPS unavailable warning */}
        {!gpsAvailable && (
          <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-yellow-700">
                  GPS is not available. You can still start/stop work, but location will not be logged.
                </p>
                <button
                  type="button"
                  onClick={() => setShowPermissionHelp(true)}
                  className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                >
                  How to enable location services
                </button>
              </div>
            </div>
          </div>
        )}

        <LocationPermissionHelp isOpen={showPermissionHelp} onClose={() => setShowPermissionHelp(false)} />

        {/* Action button */}
        <div className="flex space-x-3">
          {canStart && (
            <button
              onClick={handleStartWork}
              disabled={isLoading}
              className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCapturing ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Getting location...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {workOrderStatus === 'IN_PROGRESS' ? 'Resume Work' : 'Start Work'}
                </>
              )}
            </button>
          )}

          {canStop && (
            <button
              onClick={handleStopWork}
              disabled={isLoading}
              className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCapturing ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Getting location...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
                  </svg>
                  Stop Work
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkOrderGPSTracker;
